// @ts-strict-ignore
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { LabelStrip, LabelStripTexture } from './replace-labeling-data';
import { Connection } from '@3d-scene/model3d/connection';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { PropertyVO } from '../../article/model/vo/property.vo';
import { ConnectionAreaVO, MetaElementUsageIdentityVO, TextureAreaUsageVO } from '@shared/vo/meta-element.vo';
import { ArticleUtils } from '@article/util/article-utils';
import { LabelingUtils } from '@3d-scene/model3d/util/labeling-utils';
import { ReferenceArticleUsageUtils } from '@article/util/reference-article-usage-utils';
import { CloneUtils } from '@shared/util/clone-utils';
import { ReplaceLabelingUtils } from './replace-labeling-utils';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { TextureConnectionInfo } from '@3d-scene/model3d/texture-connection-info';
import { ArrayUtils } from '@shared/util/array-utils';
import { Line } from '@3d-scene/model/line';
import { Rectangle3D } from '@3d-scene/model/rectangle-3d';
import { Vector3 } from 'three';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';

export class ReplaceLabelingDataBuilder {
  public static buildLabelStrip(source: ArticleContainer, connectionInfoService: ConnectionInfoService, replaceLabelingUtils: ReplaceLabelingUtils): LabelStrip {
    const labelStrip: LabelStrip = new LabelStrip(connectionInfoService);
    labelStrip.originalUsageId = source.articleGUID;
    labelStrip.articleId = source.articleUsage.articleId;
    labelStrip.articleVersion = source.articleUsage.articleVersion;

    const textures = this.buildLabelStripTextures(source, false);
    labelStrip.textures = textures;
    labelStrip.hasNotConnectedReferenceIds = replaceLabelingUtils.isNotConnectedReferenceIdExists(textures);
    const backwardAcceptingIds: MetaElementUsageIdentityVO[] = ArticleUsageUtils.getBackwardConnectedConnectionUsageIds(source.articleUsage);
    labelStrip.onAdapter = replaceLabelingUtils.isStripOnAdapterByBackwardIds(backwardAcceptingIds);
    labelStrip.backwardAcceptingIds = backwardAcceptingIds;

    const givingConnections: Connection[] = this.extractGivingConnections(source);
    labelStrip.articleLine = this.calculateArticleLineByConnections(givingConnections);
    // assumed all giving connections have the same list of feature properties
    labelStrip.featureProperties = this.buildFeaturePropertyList(givingConnections[0], source.article);

    return labelStrip;
  }

  private static buildFeaturePropertyList(sourceConnection: Connection, article: ArticleVO): PropertyVO[] {
    if (!sourceConnection || !(sourceConnection.metaElementVO instanceof ConnectionAreaVO) || !article) {
      return [];
    }

    return ConnectionInfoUtils.createFeaturePropertyList(sourceConnection.metaElementVO, article);
  }

  private static extractGivingConnections(articleContainer: ArticleContainer): Connection[] {
    const givingConnections: Connection[] = [];
    const givingConnectionElements: ConnectionAreaVO[] = ArticleUtils.getConnectionElements(articleContainer.article, ConnectionAreaVO.SIDE_GIVING);
    for (const givingArea of givingConnectionElements) {
      const connection: Connection = articleContainer.findFirstConnectionByName(givingArea.name);
      if (connection) {
        givingConnections.push(connection);
      }
    }
    return ConnectionInfoUtils.sortConnections(givingConnections, true);
  }

  private static calculateArticleLineByConnections(sortedConnections: Connection[]): Line {
    if (ArrayUtils.isEmpty(sortedConnections)) {
      return null;
    }

    const firstRectangle: Rectangle3D = sortedConnections[0].compositionRectangle;
    const lastRectangle: Rectangle3D = sortedConnections[sortedConnections.length - 1].compositionRectangle;
    const startPoint: Vector3 = MathUtils.calculateMiddlePoint(firstRectangle.point1, firstRectangle.point4);
    const endPoint: Vector3 = MathUtils.calculateMiddlePoint(lastRectangle.point3, lastRectangle.point2);
    return new Line(startPoint, endPoint);
  }

  public static buildLabelStripTextures(
    articleContainer: ArticleContainer,
    useOriginalTextureUsage: boolean,
  ): LabelStripTexture[] {
    return this.getSortedTextureConnections(articleContainer).map((connection) =>
      this.buildLabelStripTexture(connection, articleContainer, useOriginalTextureUsage)
    );
  }

  public static getSortedTextureConnections(articleContainer: ArticleContainer): Connection[] {
    const textureConnections: Connection[] = this.extractTextureConnections(articleContainer);
    return ConnectionInfoUtils.sortConnections(textureConnections);
  }

  public static extractTextureConnections(articleContainer: ArticleContainer): Connection[] {
    return articleContainer.connections.filter((connection) => connection.metaElementUsage instanceof TextureAreaUsageVO);
  }

  private static buildLabelStripTexture(
    sourceConnection: Connection,
    sourceContainer: ArticleContainer,
    useOriginalTextureUsage: boolean
  ): LabelStripTexture {
    const texture: LabelStripTexture = this.buildLabelStripTextureByUsage(
      sourceConnection.metaElementUsage as TextureAreaUsageVO,
      sourceContainer.articleUsage,
      useOriginalTextureUsage
    );
    const connectionInfo: TextureConnectionInfo = sourceContainer.findTextureConnectionInfo(sourceConnection.connectionName);
    if (connectionInfo) {
      texture.localRectangle = connectionInfo.rectangle.clone();
      texture.distanceToPreviousConnection = connectionInfo.distanceToPreviousConnection;
    }
    return texture;
  }

  public static buildLabelStripTextureByUsage(
    textureUsage: TextureAreaUsageVO,
    articleUsage: ArticleUsageVO,
    useOriginalTextureUsage: boolean
  ): LabelStripTexture {
    const texture: LabelStripTexture = new LabelStripTexture();
    texture.usage = useOriginalTextureUsage ? textureUsage : CloneUtils.cloneTextureAreaUsageVO(textureUsage);
    const directAcceptingId: MetaElementUsageIdentityVO = LabelingUtils.findAcceptingAreaIdByRelativeConnectionByUsage(
      articleUsage,
      textureUsage.identity.metaElementName
    );
    const referenceUsageId: string = ReferenceArticleUsageUtils.getBackwardReferenceArticleUsageId(
      articleUsage,
      textureUsage.identity.metaElementName
    );
    texture.directAcceptingId = directAcceptingId;
    texture.referenceId = !!referenceUsageId ? MetaElementUsageIdentityVO.create(referenceUsageId, null) : null;
    return texture;
  }
}
